import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getCompanySettings } from '@/utils/companySettings';
import { Stage, Layer, Rect, Text, Image as KonvaImage, Transformer, Line } from 'react-konva';
import { ArrowLeft, Save, Download, HelpCircle, Info, X, Keyboard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import HelpIcon from '@/components/HelpIcon';
import PullProjectImagesPicker from '../components/PullProjectImagesPicker';
import { Brand } from '@/components/brand/Brand';

// Import modular components
import {
  CANVAS_PRESETS,
  MoodBoardToolbar,
  PropertiesPanel,
  ShapeLibrarySheet,
  ClipsPanel,
  AddExternalImageDialog,
  CutoutDialog,
  renderGrid,
  renderSpecTags,
  renderLegend,
  renderWatermark,
  renderElement,
} from '../components/moodboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MoodBoardPage({ isPopout = false, darkMode: propDarkMode = null, projectIdProp = null }) {
  const { projectId: projectIdParam, moodBoardId } = useParams();
  const projectId = projectIdProp || projectIdParam;
  const navigate = useNavigate();
  
  // Core state
  const [moodBoard, setMoodBoard] = useState(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Canvas state
  const [zoom, setZoom] = useState(0.6);
  const [showGrid, setShowGrid] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  
  // Tool state
  const [activeTool, setActiveTool] = useState('select');
  const [cutoutPoints, setCutoutPoints] = useState([]);
  const [cutoutTargetId, setCutoutTargetId] = useState(null);
  const [showCutoutDialog, setShowCutoutDialog] = useState(false);
  
  // UI state
  const [showClipsPanel, setShowClipsPanel] = useState(false);
  const [showPullPicker, setShowPullPicker] = useState(false);
  const [showPropertiesPanel, setShowPropertiesPanel] = useState(true);
  const [showAddImageDialog, setShowAddImageDialog] = useState(false);
  const [externalImageUrl, setExternalImageUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Shape drawing state
  const [shapeFillColor, setShapeFillColor] = useState('#BC5D41');
  const [shapeStrokeColor, setShapeStrokeColor] = useState('#1A1918');
  const [showShapeLibrary, setShowShapeLibrary] = useState(false);
  
  // SpecNum tag settings
  const [showSpecTags, setShowSpecTags] = useState(true);
  const [specTagOpacity, setSpecTagOpacity] = useState(1);
  const [specTagFontColor, setSpecTagFontColor] = useState('#1A1918');
  const [specTagBgColor, setSpecTagBgColor] = useState('#FFFFFF');
  const [specTagBorderColor, setSpecTagBorderColor] = useState('#BC5D41');
  const [specTagShowBox, setSpecTagShowBox] = useState(true);
  const [showLegend, setShowLegend] = useState(false);
  const [legendPosition, setLegendPosition] = useState({ x: 50, y: 100 });
  
  // Watermark settings
  const [showWatermark, setShowWatermark] = useState(false);
  const [watermarkOpacity, setWatermarkOpacity] = useState(0.15);
  
  // Logo image
  const [logoImage, setLogoImage] = useState(null);
  // DP64 Part 4 — a mood board is PRINTED and SENT. Its header carries the
  // TENANT's logo and name, or the words "Company Logo Here". It used to draw
  // the app's own root-level mark file and one firm's name on every tenant's
  // board, plus our wordmark.
  const [companySettings, setCompanySettings] = useState(null);
  
  // Help/Training mode
  const [showHelp, setShowHelp] = useState(false);
  
  // Keyboard shortcuts dialog
  const [showShortcutsDialog, setShowShortcutsDialog] = useState(false);
  
  // Track last click position on canvas for placing new elements
  const [lastCanvasClick, setLastCanvasClick] = useState({ x: 200, y: 200 });
  
  // File input ref
  const fileInputRef = useRef(null);
  
  // Clips for adding to board
  const [availableClips, setAvailableClips] = useState([]);
  
  // Image cache
  const [loadedImages, setLoadedImages] = useState({});
  
  // Refs
  const stageRef = useRef(null);
  const transformerRef = useRef(null);
  const containerRef = useRef(null);
  
  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, [projectId, moodBoardId]);

  useEffect(() => {
    getCompanySettings()
      .then((d) => setCompanySettings(d))
      .catch(() => setCompanySettings(null));
  }, []);

  useEffect(() => {
    const url = companySettings?.logo_url;
    if (!url) { setLogoImage(null); return; }
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => setLogoImage(img);
    img.onerror = () => setLogoImage(null);
    img.src = url.startsWith('/') ? `${BACKEND_URL}${url}` : url;
  }, [companySettings?.logo_url]);
  
  const fetchData = async () => {
    try {
      setLoading(true);
      
      const projectRes = await axios.get(`${API}/projects/${projectId}`);
      setProject(projectRes.data);
      
      if (moodBoardId && moodBoardId !== 'new') {
        const mbRes = await axios.get(`${API}/mood-boards/${moodBoardId}`);
        setMoodBoard(mbRes.data);
      } else {
        const newMb = {
          project_id: projectId,
          name: `${projectRes.data.name} - Mood Board`,
          canvas_width: CANVAS_PRESETS['letter-landscape'].width,
          canvas_height: CANVAS_PRESETS['letter-landscape'].height,
          elements: []
        };
        const createRes = await axios.post(`${API}/projects/${projectId}/mood-boards`, newMb);
        setMoodBoard(createRes.data);
        navigate(`/projects/${projectId}/mood-boards/${createRes.data.id}`, { replace: true });
      }
      
      const clipsRes = await axios.get(`${API}/projects/${projectId}/clips-for-mood-board`);
      setAvailableClips(clipsRes.data.clips || []);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load mood board');
    } finally {
      setLoading(false);
    }
  };
  
  // Load images when elements change
  useEffect(() => {
    if (!moodBoard?.elements) return;
    
    moodBoard.elements.forEach(el => {
      if (el.element_type === 'image' && el.image_url && !loadedImages[el.image_url]) {
        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          setLoadedImages(prev => ({ ...prev, [el.image_url]: img }));
        };
        img.onerror = () => {
          console.error('Failed to load image:', el.image_url);
        };
        img.src = el.image_url;
      }
    });
  }, [moodBoard?.elements]);
  
  // Save mood board
  // Wave 5.5 (2026-02) — auto-save vs manual-save toast fix.
  //   - Manual save (Ctrl+S / Save button): success + error toasts (user
  //     triggered it, they want feedback).
  //   - Auto-save (2s debounce after edits): silent. Success/failure
  //     goes to console only; the user hasn't asked for a status ping
  //     and back-to-back saves were creating a "Failed to save" toast
  //     race after PULL-image inserts.
  // A `savingRef` also prevents overlapping PUTs when edits arrive
  // faster than the network round-trip.
  const savingRef = useRef(false);
  const saveMoodBoard = async ({ silent = false } = {}) => {
    if (!moodBoard) return;
    if (savingRef.current) return;
    savingRef.current = true;

    try {
      setSaving(true);
      await axios.put(`${API}/mood-boards/${moodBoard.id}`, {
        name: moodBoard.name,
        elements: moodBoard.elements,
        canvas_width: moodBoard.canvas_width,
        canvas_height: moodBoard.canvas_height,
        background_color: moodBoard.background_color,
        show_header: moodBoard.show_header,
        show_footer: moodBoard.show_footer,
        disclosure_text: moodBoard.disclosure_text
      });
      if (!silent) toast.success('Mood board saved');
    } catch (error) {
      console.error('Error saving:', error);
      if (!silent) toast.error('Failed to save');
    } finally {
      setSaving(false);
      savingRef.current = false;
    }
  };
  
  // Auto-save on changes
  useEffect(() => {
    if (!moodBoard?.id || loading) return;
    
    const timeout = setTimeout(() => {
      saveMoodBoard({ silent: true });
    }, 2000);
    
    return () => clearTimeout(timeout);
  }, [moodBoard?.elements, moodBoard?.name]);
  
  // Attach transformer to selected node
  useEffect(() => {
    if (selectedId && transformerRef.current && stageRef.current) {
      const stage = stageRef.current;
      const selectedNode = stage.findOne('#' + selectedId);
      if (selectedNode) {
        transformerRef.current.nodes([selectedNode]);
        transformerRef.current.getLayer()?.batchDraw();
      } else {
        transformerRef.current.nodes([]);
      }
    } else if (transformerRef.current) {
      transformerRef.current.nodes([]);
    }
  }, [selectedId, moodBoard?.elements]);
  
  // Keyboard shortcuts
  const handleKeyDown = useCallback((e) => {
    // Don't trigger shortcuts when typing in inputs
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
    
    const key = e.key.toLowerCase();
    const ctrlOrCmd = e.ctrlKey || e.metaKey;
    
    // Ctrl+S - Save
    if (ctrlOrCmd && key === 's') {
      e.preventDefault();
      saveMoodBoard();
      return;
    }
    
    // Escape - Deselect
    if (key === 'escape') {
      setSelectedId(null);
      setShowCutoutDialog(false);
      setShowShapeLibrary(false);
      setShowClipsPanel(false);
      return;
    }
    
    // ? - Show shortcuts
    if (key === '?' || (e.shiftKey && key === '/')) {
      setShowShortcutsDialog(true);
      return;
    }
    
    // G - Toggle grid
    if (key === 'g' && !ctrlOrCmd) {
      setShowGrid(prev => !prev);
      return;
    }
    
    // T - Add text
    if (key === 't' && !ctrlOrCmd) {
      addTextElement();
      return;
    }
    
    // R - Add rectangle
    if (key === 'r' && !ctrlOrCmd) {
      addShapeElement('rectangle');
      return;
    }
    
    // E - Add ellipse  
    if (key === 'e' && !ctrlOrCmd) {
      addShapeElement('ellipse');
      return;
    }
    
    // V - Select tool
    if (key === 'v' && !ctrlOrCmd) {
      setActiveTool('select');
      return;
    }
    
    // Shortcuts requiring selected element
    if (selectedId && moodBoard?.elements) {
      const selectedElement = moodBoard.elements.find(el => el.id === selectedId);
      if (!selectedElement) return;
      
      // Delete/Backspace - Delete element
      if (key === 'delete' || key === 'backspace') {
        e.preventDefault();
        deleteElement(selectedId);
        return;
      }
      
      // Ctrl+D - Duplicate
      if (ctrlOrCmd && key === 'd') {
        e.preventDefault();
        duplicateElement(selectedId);
        return;
      }
      
      // Arrow keys - Nudge
      const nudgeAmount = e.shiftKey ? 10 : 1;
      if (key === 'arrowup') {
        e.preventDefault();
        updateElement(selectedId, { y: selectedElement.y - nudgeAmount });
        return;
      }
      if (key === 'arrowdown') {
        e.preventDefault();
        updateElement(selectedId, { y: selectedElement.y + nudgeAmount });
        return;
      }
      if (key === 'arrowleft') {
        e.preventDefault();
        updateElement(selectedId, { x: selectedElement.x - nudgeAmount });
        return;
      }
      if (key === 'arrowright') {
        e.preventDefault();
        updateElement(selectedId, { x: selectedElement.x + nudgeAmount });
        return;
      }
      
      // [ - Send backward
      if (key === '[') {
        sendBackward(selectedId);
        return;
      }
      
      // ] - Bring forward
      if (key === ']') {
        bringForward(selectedId);
        return;
      }
    }
  }, [selectedId, moodBoard?.elements]);
  
  // Attach keyboard listener
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
  
  // Element operations
  const addElement = (element) => {
    const newElement = {
      id: `el-${Date.now()}`,
      z_index: (moodBoard?.elements?.length || 0),
      ...element
    };
    
    setMoodBoard(prev => ({
      ...prev,
      elements: [...(prev.elements || []), newElement]
    }));
    
    setSelectedId(newElement.id);
  };
  
  const addImageFromClip = (clip) => {
    const imageUrl = clip.primary_image;
    if (!imageUrl) {
      toast.error('No image available for this clip');
      return;
    }
    
    addElement({
      element_type: 'image',
      image_url: imageUrl,
      clip_id: clip.id,
      clip_title: clip.title,
      spec_num: clip.spec_num,
      spec_category: clip.spec_category,
      x: 100 + Math.random() * 200,
      y: 100 + Math.random() * 200,
      width: 300,
      height: 300,
      opacity: 1
    });
    
    toast.success(`Added: ${clip.title}${clip.spec_num ? ` (${clip.spec_num})` : ''}`);
  };

  // Wave 5.1 (2026-02) — PULL image picker. Handles a single pick from
  // the shared PullProjectImagesPicker. Provenance is preserved on the
  // element so future exports / audits can trace back to the origin.
  const addImageFromProjectPhoto = async (pick) => {
    const imageUrl = pick.image_url;
    if (!imageUrl) return;
    addElement({
      element_type: 'image',
      image_url: imageUrl,
      source_photo_id: pick.source_photo_id,
      source_type: pick.source,
      source_visit_id: pick.visit_id,
      clip_title: pick.caption || 'Project photo',
      x: 100 + Math.random() * 200,
      y: 100 + Math.random() * 200,
      width: 300,
      height: 300,
      opacity: 1,
    });
  };
  
  const addExternalImage = () => {
    if (!externalImageUrl.trim()) {
      toast.error('Please enter an image URL');
      return;
    }
    
    addElement({
      element_type: 'image',
      image_url: externalImageUrl.trim(),
      x: 100 + Math.random() * 200,
      y: 100 + Math.random() * 200,
      width: 300,
      height: 300
    });
    
    setExternalImageUrl('');
    setShowAddImageDialog(false);
    toast.success('Image added');
  };
  
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, GIF, WebP, or PDF file');
      return;
    }
    
    try {
      setUploadingImage(true);
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/mood-boards/${moodBoard.id}/upload-image`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      const imageUrl = `${BACKEND_URL}${response.data.url}`;
      addElement({
        element_type: 'image',
        image_url: imageUrl,
        x: 100 + Math.random() * 200,
        y: 100 + Math.random() * 200,
        width: 300,
        height: 300
      });
      
      toast.success(`Uploaded: ${file.name}`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  const addShapeElement = (shapeType, options = {}) => {
    const baseProps = {
      element_type: 'shape',
      shape_type: shapeType,
      x: lastCanvasClick.x,
      y: lastCanvasClick.y,
      width: shapeType === 'line' ? 200 : 150,
      height: shapeType === 'line' ? 4 : (options.aspectRatio === 1 ? 150 : 100),
      fill_color: shapeType === 'line' ? null : shapeFillColor,
      stroke_color: shapeStrokeColor,
      stroke_width: 2,
      rotation: options.rotation || 0,
      opacity: 1,
      corner_radius: options.cornerRadius || 0,
      polygon_points: options.points || null,
      svg_path: options.path || null,
    };
    
    addElement(baseProps);
  };
  
  const addShapeFromLibrary = (shape) => {
    addShapeElement(shape.type, {
      aspectRatio: shape.aspectRatio,
      cornerRadius: shape.cornerRadius,
      rotation: shape.rotation,
      points: shape.points,
      path: shape.path,
    });
    setShowShapeLibrary(false);
  };
  
  const addIconFromLibrary = (icon) => {
    addElement({
      element_type: 'shape',
      shape_type: 'path',
      x: lastCanvasClick.x,
      y: lastCanvasClick.y,
      width: 80,
      height: 80,
      fill_color: shapeFillColor,
      stroke_color: shapeStrokeColor,
      stroke_width: 2,
      rotation: 0,
      opacity: 1,
      svg_path: icon.path,
      icon_name: icon.name,
    });
    setShowShapeLibrary(false);
  };
  
  const addTextElement = () => {
    addElement({
      element_type: 'text',
      text: 'Double-click to edit',
      x: lastCanvasClick.x,
      y: lastCanvasClick.y,
      width: 200,
      height: 30,
      font_family: 'Manrope',
      font_size: 24,
      font_weight: 'normal',
      font_style: 'normal',
      text_color: '#1A1918',
      text_align: 'left',
      show_leader: false,
      leader_start_x: 100,
      leader_start_y: 38,
      leader_curve1_x: 120,
      leader_curve1_y: 60,
      leader_curve2_x: 80,
      leader_curve2_y: 100,
      leader_end_x: 120,
      leader_end_y: 130,
      leader_color: '#1A1918',
      leader_width: 2,
      show_arrow: true
    });
  };
  
  const updateElement = (id, updates) => {
    setMoodBoard(prev => ({
      ...prev,
      elements: prev.elements.map(el => 
        el.id === id ? { ...el, ...updates } : el
      )
    }));
  };
  
  const deleteElement = (id) => {
    setMoodBoard(prev => ({
      ...prev,
      elements: prev.elements.filter(el => el.id !== id)
    }));
    setSelectedId(null);
  };
  
  const duplicateElement = (id) => {
    const element = moodBoard.elements.find(el => el.id === id);
    if (!element) return;
    
    addElement({
      ...element,
      id: undefined,
      x: element.x + 20,
      y: element.y + 20
    });
  };
  
  // Layer ordering
  const bringToFront = (id) => {
    const maxZ = Math.max(...moodBoard.elements.map(el => el.z_index || 0));
    updateElement(id, { z_index: maxZ + 1 });
  };
  
  const sendToBack = (id) => {
    const minZ = Math.min(...moodBoard.elements.map(el => el.z_index || 0));
    updateElement(id, { z_index: minZ - 1 });
  };
  
  const bringForward = (id) => {
    const element = moodBoard.elements.find(el => el.id === id);
    if (!element) return;
    updateElement(id, { z_index: (element.z_index || 0) + 1 });
  };
  
  const sendBackward = (id) => {
    const element = moodBoard.elements.find(el => el.id === id);
    if (!element) return;
    updateElement(id, { z_index: (element.z_index || 0) - 1 });
  };
  
  // Canvas operations
  const fitToView = () => {
    if (!containerRef.current || !moodBoard) return;
    const containerWidth = containerRef.current.clientWidth - 64;
    const containerHeight = containerRef.current.clientHeight - 64;
    const canvasWidth = moodBoard.canvas_width || 1650;
    const canvasHeight = moodBoard.canvas_height || 1275;
    const scaleX = containerWidth / canvasWidth;
    const scaleY = containerHeight / canvasHeight;
    const newZoom = Math.min(scaleX, scaleY, 1);
    setZoom(Math.max(0.25, Math.min(1, newZoom)));
  };
  
  // Cutout operations
  const startCutout = (id) => {
    const element = moodBoard.elements.find(el => el.id === id);
    if (!element || element.element_type !== 'image') {
      toast.error('Select an image to cutout');
      return;
    }
    const img = loadedImages[element.image_url];
    if (!img) {
      toast.error('Image not loaded. The image may be inaccessible due to CORS restrictions.');
      return;
    }
    setCutoutTargetId(id);
    setCutoutPoints(element.crop_points || []);
    setShowCutoutDialog(true);
    setActiveTool('cutout');
  };
  
  const applyCutout = () => {
    if (cutoutPoints.length < 3) {
      toast.error('Draw at least 3 points to create a cutout');
      return;
    }
    
    updateElement(cutoutTargetId, {
      crop_points: cutoutPoints
    });
    
    setCutoutTargetId(null);
    setCutoutPoints([]);
    setShowCutoutDialog(false);
    setActiveTool('select');
    toast.success('Cutout applied');
  };
  
  const clearCutout = (id) => {
    updateElement(id, { crop_points: null });
    toast.success('Cutout removed');
  };
  
  const cancelCutout = () => {
    setCutoutTargetId(null);
    setCutoutPoints([]);
    setShowCutoutDialog(false);
    setActiveTool('select');
  };
  
  // Event handlers
  const handleStageClick = (e) => {
    const stage = e.target.getStage();
    const pos = stage.getPointerPosition();
    if (pos) {
      const canvasX = pos.x / zoom;
      const canvasY = pos.y / zoom;
      setLastCanvasClick({ x: canvasX, y: canvasY });
    }
    
    if (e.target === stage) {
      setSelectedId(null);
    }
  };
  
  const handleDragEnd = (e, id) => {
    updateElement(id, {
      x: e.target.x(),
      y: e.target.y()
    });
  };
  
  const handleTransformEnd = (e, id) => {
    const node = e.target;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    
    node.scaleX(1);
    node.scaleY(1);
    
    updateElement(id, {
      x: node.x(),
      y: node.y(),
      width: Math.max(20, node.width() * scaleX),
      height: Math.max(20, node.height() * scaleY),
      rotation: node.rotation()
    });
  };
  
  const exportToImage = () => {
    if (!stageRef.current) return;
    
    const wasGridShown = showGrid;
    const wasSelected = selectedId;
    setShowGrid(false);
    setSelectedId(null);
    
    setTimeout(() => {
      const uri = stageRef.current.toDataURL({
        pixelRatio: 2,
        mimeType: 'image/png'
      });
      
      const link = document.createElement('a');
      link.download = `${moodBoard?.name || 'mood-board'}.png`;
      link.href = uri;
      link.click();
      
      setShowGrid(wasGridShown);
      setSelectedId(wasSelected);
      
      toast.success('Mood board exported');
    }, 100);
  };
  
  // Get selected element
  const selectedElement = moodBoard?.elements?.find(el => el.id === selectedId);
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading mood board...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#F5F5F4] flex flex-col" data-testid="mood-board-page">
      {/* Header */}
      <header className="bg-white border-b border-border px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/projects/${projectId}/hub`)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-3">
            {/* The old <img> hid itself onError — the mark is not allowed to
                collapse away (design pass 1 part D). */}
            <Brand size="nav" variant="mark" testId="moodboard-brand" />
            <div className="flex items-center gap-2">
              <HelpIcon elementId="panel-builder" position="inline" />
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-heading text-foreground" data-testid="moodboard-company-name">{companySettings?.company_name || ''}</span>
                </div>
                <Input
                  value={moodBoard?.name || ''}
                  onChange={(e) => setMoodBoard(prev => ({ ...prev, name: e.target.value }))}
                  className="text-sm border-none shadow-none focus-visible:ring-0 p-0 h-auto text-muted-foreground"
                  placeholder="Mood Board Name"
                  data-testid="mood-board-name-input"
                />
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowShortcutsDialog(true)}
                  data-testid="shortcuts-btn"
                >
                  <Keyboard className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Keyboard Shortcuts (?)</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant={showHelp ? 'secondary' : 'ghost'} 
                  size="sm" 
                  onClick={() => setShowHelp(!showHelp)}
                  data-testid="help-toggle-btn"
                >
                  <HelpCircle className="w-4 h-4 mr-1" />
                  <span className="text-xs">Help</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Toggle Training Tips</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Button variant="outline" size="sm" onClick={saveMoodBoard} disabled={saving} data-testid="save-btn">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save'}
          </Button>
          <Button variant="outline" size="sm" onClick={exportToImage} data-testid="export-btn">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </header>
      
      {/* Help/Training Panel */}
      {showHelp && (
        <div className="bg-blue-50 border-b border-blue-200 px-4 py-2">
          <div className="flex items-start gap-4 max-w-4xl mx-auto">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <p className="font-medium text-blue-900 mb-1">Training Mode Active</p>
              <p className="text-blue-700 text-xs">
                Look for <HelpCircle className="w-3 h-3 inline" /> icons throughout the interface. 
                Hover over them for detailed instructions. 
                <strong> Tip:</strong> Click on the canvas where you want new items to appear before adding them.
              </p>
            </div>
            <button onClick={() => setShowHelp(false)} className="text-blue-600 hover:text-blue-800">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      
      {/* Toolbar */}
      <MoodBoardToolbar
        activeTool={activeTool}
        setActiveTool={setActiveTool}
        addTextElement={addTextElement}
        setShowClipsPanel={setShowClipsPanel}
        fileInputRef={fileInputRef}
        uploadingImage={uploadingImage}
        addShapeElement={addShapeElement}
        setShowShapeLibrary={setShowShapeLibrary}
        shapeFillColor={shapeFillColor}
        setShapeFillColor={setShapeFillColor}
        showGrid={showGrid}
        setShowGrid={setShowGrid}
        showSpecTags={showSpecTags}
        setShowSpecTags={setShowSpecTags}
        showLegend={showLegend}
        setShowLegend={setShowLegend}
        showWatermark={showWatermark}
        setShowWatermark={setShowWatermark}
        zoom={zoom}
        setZoom={setZoom}
        fitToView={fitToView}
        selectedElement={selectedElement}
        selectedId={selectedId}
        startCutout={startCutout}
        bringToFront={bringToFront}
        bringForward={bringForward}
        sendBackward={sendBackward}
        sendToBack={sendToBack}
        duplicateElement={duplicateElement}
        deleteElement={deleteElement}
        onPullFromProject={() => setShowPullPicker(true)}
      />
      
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
        onChange={handleFileUpload}
        className="hidden"
      />
      
      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Canvas area */}
        <div 
          ref={containerRef}
          className="flex-1 overflow-auto p-8 flex items-start justify-center"
          style={{ background: '#E5E2DD' }}
        >
          <div 
            className="shadow-2xl"
            style={{
              width: (moodBoard?.canvas_width || 1650) * zoom,
              height: (moodBoard?.canvas_height || 1275) * zoom
            }}
          >
            <Stage
              ref={stageRef}
              width={(moodBoard?.canvas_width || 1650) * zoom}
              height={(moodBoard?.canvas_height || 1275) * zoom}
              scaleX={zoom}
              scaleY={zoom}
              onClick={handleStageClick}
              style={{ background: moodBoard?.background_color || '#FFFFFF' }}
            >
              <Layer>
                {/* Background */}
                <Rect
                  x={0}
                  y={0}
                  width={moodBoard?.canvas_width || 1650}
                  height={moodBoard?.canvas_height || 1275}
                  fill={moodBoard?.background_color || '#FFFFFF'}
                  listening={false}
                />
                
                {/* Grid overlay */}
                {renderGrid(moodBoard, showGrid)}
                
                {/* Header */}
                {moodBoard?.show_header !== false && (
                  <>
                    <Rect x={0} y={0} width={moodBoard?.canvas_width || 1650} height={90} fill="#FFFFFF" listening={false} />
                    <Line points={[0, 90, moodBoard?.canvas_width || 1650, 90]} stroke="#E5E2DD" strokeWidth={1} listening={false} />
                    {logoImage ? (
                      <KonvaImage
                        image={logoImage}
                        x={30}
                        y={12}
                        width={60}
                        height={60}
                        listening={false}
                      />
                    ) : (
                      <Text
                        x={30}
                        y={36}
                        text="Company Logo Here"
                        fontSize={11}
                        fontFamily="Manrope"
                        fill="#B4B4B4"
                        listening={false}
                      />
                    )}
                    <Text
                      x={100}
                      y={18}
                      text={companySettings?.company_name || ''}
                      fontSize={26}
                      fontFamily="Cormorant Garamond"
                      fill="#1A1918"
                      listening={false}
                    />
                    <Text
                      x={100}
                      y={62}
                      text={project?.name || 'Project'}
                      fontSize={11}
                      fontFamily="Manrope"
                      fill="#78716C"
                      letterSpacing={1}
                      listening={false}
                    />
                    <Text
                      x={(moodBoard?.canvas_width || 1650) - 200}
                      y={30}
                      width={160}
                      text={project?.client?.name || project?.client_name || ''}
                      fontSize={11}
                      fontFamily="Manrope"
                      fill="#78716C"
                      align="right"
                      listening={false}
                    />
                  </>
                )}
                
                {/* Watermark (behind elements) */}
                {renderWatermark({
                  moodBoard,
                  showWatermark,
                  watermarkOpacity,
                  logoImage,
                })}
                
                {/* Elements */}
                {moodBoard?.elements
                  ?.sort((a, b) => (a.z_index || 0) - (b.z_index || 0))
                  .map(el => renderElement({
                    el,
                    loadedImages,
                    selectedId,
                    activeTool,
                    setSelectedId,
                    updateElement,
                    handleDragEnd,
                    handleTransformEnd,
                  }))}
                
                {/* SpecNum Tags */}
                {renderSpecTags({
                  moodBoard,
                  showSpecTags,
                  specTagOpacity,
                  specTagFontColor,
                  specTagBgColor,
                  specTagBorderColor,
                  specTagShowBox,
                })}
                
                {/* Legend Box */}
                {renderLegend({
                  moodBoard,
                  showLegend,
                  legendPosition,
                  setLegendPosition,
                })}
                
                {/* Footer */}
                {moodBoard?.show_footer !== false && (
                  <>
                    <Rect 
                      x={0} 
                      y={(moodBoard?.canvas_height || 1275) - 60} 
                      width={moodBoard?.canvas_width || 1650} 
                      height={60} 
                      fill="#FDFCFB" 
                      listening={false} 
                    />
                    <Line 
                      points={[0, (moodBoard?.canvas_height || 1275) - 60, moodBoard?.canvas_width || 1650, (moodBoard?.canvas_height || 1275) - 60]} 
                      stroke="#E5E2DD" 
                      strokeWidth={1} 
                      listening={false} 
                    />
                    <Text
                      x={40}
                      y={(moodBoard?.canvas_height || 1275) - 50}
                      width={(moodBoard?.canvas_width || 1650) - 80}
                      text={moodBoard?.disclosure_text || 'Product specifications subject to change.'}
                      fontSize={8}
                      fontFamily="Manrope"
                      fill="#78716C"
                      lineHeight={1.4}
                      listening={false}
                    />
                  </>
                )}
                
                {/* Transformer */}
                <Transformer
                  ref={transformerRef}
                  rotateEnabled={true}
                  enabledAnchors={selectedElement?.element_type === 'line' 
                    ? ['middle-left', 'middle-right'] 
                    : ['top-left', 'top-right', 'bottom-left', 'bottom-right', 'middle-left', 'middle-right', 'top-center', 'bottom-center']}
                  keepRatio={selectedElement?.element_type === 'image'}
                  boundBoxFunc={(oldBox, newBox) => {
                    if (newBox.width < 20 || newBox.height < 20) {
                      return oldBox;
                    }
                    return newBox;
                  }}
                />
              </Layer>
            </Stage>
          </div>
        </div>
        
        {/* Properties panel */}
        {showPropertiesPanel && selectedElement && (
          <PropertiesPanel
            selectedElement={selectedElement}
            selectedId={selectedId}
            updateElement={updateElement}
            deleteElement={deleteElement}
            startCutout={startCutout}
            clearCutout={clearCutout}
            showHelp={showHelp}
            showSpecTags={showSpecTags}
            setShowSpecTags={setShowSpecTags}
            showLegend={showLegend}
            setShowLegend={setShowLegend}
            specTagShowBox={specTagShowBox}
            setSpecTagShowBox={setSpecTagShowBox}
            specTagOpacity={specTagOpacity}
            setSpecTagOpacity={setSpecTagOpacity}
            specTagFontColor={specTagFontColor}
            setSpecTagFontColor={setSpecTagFontColor}
            specTagBgColor={specTagBgColor}
            setSpecTagBgColor={setSpecTagBgColor}
            specTagBorderColor={specTagBorderColor}
            setSpecTagBorderColor={setSpecTagBorderColor}
            showWatermark={showWatermark}
            setShowWatermark={setShowWatermark}
            watermarkOpacity={watermarkOpacity}
            setWatermarkOpacity={setWatermarkOpacity}
            bringToFront={bringToFront}
            bringForward={bringForward}
            sendBackward={sendBackward}
            sendToBack={sendToBack}
          />
        )}
      </div>
      
      {/* Dialogs and Sheets */}
      <ShapeLibrarySheet
        open={showShapeLibrary}
        onOpenChange={setShowShapeLibrary}
        addShapeFromLibrary={addShapeFromLibrary}
        addIconFromLibrary={addIconFromLibrary}
      />
      
      <ClipsPanel
        open={showClipsPanel}
        onOpenChange={setShowClipsPanel}
        availableClips={availableClips}
        addImageFromClip={addImageFromClip}
      />

      {/* Wave 5.1 (2026-02) — PULL image picker: browse the union of
          field / desktop / email photos for this project. */}
      <PullProjectImagesPicker
        open={showPullPicker}
        onClose={() => setShowPullPicker(false)}
        projectId={projectId}
        onPick={addImageFromProjectPhoto}
      />
      
      <AddExternalImageDialog
        open={showAddImageDialog}
        onOpenChange={setShowAddImageDialog}
        externalImageUrl={externalImageUrl}
        setExternalImageUrl={setExternalImageUrl}
        addExternalImage={addExternalImage}
      />
      
      <CutoutDialog
        open={showCutoutDialog}
        onOpenChange={setShowCutoutDialog}
        cutoutTargetId={cutoutTargetId}
        moodBoard={moodBoard}
        loadedImages={loadedImages}
        cutoutPoints={cutoutPoints}
        setCutoutPoints={setCutoutPoints}
        applyCutout={applyCutout}
        cancelCutout={cancelCutout}
      />
      
      {/* Keyboard Shortcuts Dialog */}
      <Dialog open={showShortcutsDialog} onOpenChange={setShowShortcutsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Keyboard className="w-5 h-5" />
              Keyboard Shortcuts
            </DialogTitle>
            <DialogDescription>
              Use these shortcuts to work faster in the Mood Board editor.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <h4 className="font-medium text-sm mb-2 text-muted-foreground">General</h4>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span>Save</span>
                  <kbd className="px-2 py-0.5 bg-muted rounded text-xs font-mono">Ctrl+S</kbd>
                </div>
                <div className="flex justify-between">
                  <span>Deselect / Close dialogs</span>
                  <kbd className="px-2 py-0.5 bg-muted rounded text-xs font-mono">Esc</kbd>
                </div>
                <div className="flex justify-between">
                  <span>Show shortcuts</span>
                  <kbd className="px-2 py-0.5 bg-muted rounded text-xs font-mono">?</kbd>
                </div>
                <div className="flex justify-between">
                  <span>Toggle grid</span>
                  <kbd className="px-2 py-0.5 bg-muted rounded text-xs font-mono">G</kbd>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="font-medium text-sm mb-2 text-muted-foreground">Add Elements</h4>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span>Select tool</span>
                  <kbd className="px-2 py-0.5 bg-muted rounded text-xs font-mono">V</kbd>
                </div>
                <div className="flex justify-between">
                  <span>Add text</span>
                  <kbd className="px-2 py-0.5 bg-muted rounded text-xs font-mono">T</kbd>
                </div>
                <div className="flex justify-between">
                  <span>Add rectangle</span>
                  <kbd className="px-2 py-0.5 bg-muted rounded text-xs font-mono">R</kbd>
                </div>
                <div className="flex justify-between">
                  <span>Add ellipse</span>
                  <kbd className="px-2 py-0.5 bg-muted rounded text-xs font-mono">E</kbd>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="font-medium text-sm mb-2 text-muted-foreground">Selected Element</h4>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span>Delete</span>
                  <kbd className="px-2 py-0.5 bg-muted rounded text-xs font-mono">Delete</kbd>
                </div>
                <div className="flex justify-between">
                  <span>Duplicate</span>
                  <kbd className="px-2 py-0.5 bg-muted rounded text-xs font-mono">Ctrl+D</kbd>
                </div>
                <div className="flex justify-between">
                  <span>Nudge (1px)</span>
                  <kbd className="px-2 py-0.5 bg-muted rounded text-xs font-mono">Arrow keys</kbd>
                </div>
                <div className="flex justify-between">
                  <span>Nudge (10px)</span>
                  <kbd className="px-2 py-0.5 bg-muted rounded text-xs font-mono">Shift + Arrows</kbd>
                </div>
                <div className="flex justify-between">
                  <span>Bring forward</span>
                  <kbd className="px-2 py-0.5 bg-muted rounded text-xs font-mono">]</kbd>
                </div>
                <div className="flex justify-between">
                  <span>Send backward</span>
                  <kbd className="px-2 py-0.5 bg-muted rounded text-xs font-mono">[</kbd>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
